import { CartUpdateEvent, ThemeEvents } from '@theme/events';

(() => {
  const version = 'one-sample-global-v2';

  if (window.holsGiftSamplesVersion === version) return;
  window.holsGiftSamplesVersion = version;

  const sampleVariants = {
    Amore: 42866602868832,
    'Fresh Breeze': 42866602999904,
    Horizon: 42866603327584,
    Benevolence: 42866603360352,
    Zen: 42866607980640,
    Meditate: 42866608013408,
    Spiritual: 42866608046176,
    Euphoria: 42866608341088,
    Enigma: 42866608373856,
    Tranquility: 42866608439392,
    Floret: 42866608472160,
    'Falling For You': 42866608767072,
    Serene: 42866608799840,
  };

  const freeSampleIds = Object.values(sampleVariants);

  function getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((item) => item.dataset.sectionId)
      .filter(Boolean);
  }

  function getSampleSelect() {
    return document.querySelector('#holsGiftFirst');
  }

  function getPreview() {
    return document.querySelector('#holsGiftPreview');
  }

  function setPreview(message) {
    const preview = getPreview();
    if (preview) preview.innerHTML = message;
  }

  function isSampleItem(item) {
    return freeSampleIds.includes(item.variant_id);
  }

  function hasPaidProducts(cart) {
    return cart.items.some((item) => !isSampleItem(item) && item.quantity > 0);
  }

  function selectedSampleId() {
    const name = getSampleSelect()?.value;
    return name ? sampleVariants[name] : null;
  }

  function randomSampleId() {
    return freeSampleIds[Math.floor(Math.random() * freeSampleIds.length)];
  }

  function cartPayload(body = {}) {
    const sectionIds = getCartSectionIds();

    return {
      ...body,
      ...(sectionIds.length
        ? {
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }
        : {}),
    };
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });

    const text = await response.text();

    if (!response.ok) throw new Error(text || 'Cart request failed');

    return text ? JSON.parse(text) : {};
  }

  async function getCart() {
    return await requestJSON('/cart.js', { cache: 'no-store' });
  }

  async function updateCart(updates) {
    return await requestJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cartPayload({ updates })),
    });
  }

  async function addSample(variantId) {
    return await requestJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        cartPayload({
          id: variantId,
          quantity: 1,
        })
      ),
    });
  }

  async function removeAllSamples(cart) {
    const updates = {};

    cart.items.forEach((item) => {
      if (isSampleItem(item)) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return cart;

    return await updateCart(updates);
  }

  function fixDrawerEmptyState(cart) {
    const hasItems = cart && cart.item_count > 0;

    document.querySelectorAll('.cart-drawer__dialog').forEach((dialog) => {
      dialog.classList.toggle('cart-drawer--empty', !hasItems);
      dialog.setAttribute('aria-labelledby', hasItems ? 'cart-drawer-heading' : 'cart-drawer-heading-empty');
    });

    document.querySelectorAll('.cart-drawer__heading').forEach((heading) => {
      heading.id = hasItems ? 'cart-drawer-heading' : 'cart-drawer-heading-empty';
      heading.classList.toggle('cart-drawer__heading--empty', !hasItems);

      if (hasItems && heading.textContent.trim().toLowerCase().includes('empty')) {
        heading.textContent = 'Cart';
      }

      if (!hasItems) {
        heading.textContent = 'Your cart is empty';
      }
    });
  }

  function dispatchThemeCartUpdate(cartResponse) {
    const cart = cartResponse.items ? cartResponse : null;
    const sections = cartResponse.sections || {};

    if (cart) fixDrawerEmptyState(cart);

    document.dispatchEvent(
      new CartUpdateEvent(cart || {}, 'hols-gift-samples', {
        source: 'holsGiftSamples',
        sections,
        itemCount: cart?.item_count,
      })
    );
  }

  async function repairDrawerStateSoon() {
    [50, 250, 700].forEach((delay) => {
      setTimeout(async () => {
        try {
          const cart = await getCart();
          fixDrawerEmptyState(cart);
        } catch (error) {
          console.warn('Unable to sync cart drawer state', error);
        }
      }, delay);
    });
  }

  async function syncSamples() {
    try {
      let cart = await getCart();

      fixDrawerEmptyState(cart);

      if (!hasPaidProducts(cart)) {
        cart = await removeAllSamples(cart);
        setPreview('Select your complimentary sample');
        dispatchThemeCartUpdate(cart);
        return cart;
      }

      const sampleItems = cart.items.filter(isSampleItem);

      if (sampleItems.length > 1) {
        const updates = {};

        sampleItems.slice(1).forEach((item) => {
          updates[item.key] = 0;
        });

        cart = await updateCart(updates);
        dispatchThemeCartUpdate(cart);
      }

      return cart;
    } catch (error) {
      console.error(error);
    }
  }

  async function selectSample(sampleName) {
    if (!sampleName || window.holsGiftSampleUpdating) return;

    const variantId = sampleVariants[sampleName];
    if (!variantId) return;

    window.holsGiftSampleUpdating = true;
    setPreview('Adding <strong>' + sampleName + '</strong>...');

    try {
      const cart = await getCart();

      if (!hasPaidProducts(cart)) {
        setPreview('Add a main product before choosing a sample');
        return;
      }

      await removeAllSamples(cart);

      const addResponse = await addSample(variantId);
      const updatedCart = await getCart();

      setPreview('&check; Selected Sample: <strong>' + sampleName + '</strong>');

      dispatchThemeCartUpdate({
        ...updatedCart,
        sections: addResponse.sections,
      });
    } catch (error) {
      console.error(error);
      setPreview('Could not add sample. Please try again.');
    } finally {
      window.holsGiftSampleUpdating = false;
    }
  }

  async function prepareCheckout(event, button) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }

    if (!button || button.dataset.holsGiftLoading === 'true') return false;

    const originalText = button.dataset.checkoutLabel || button.dataset.holsGiftOriginalText || button.textContent.trim() || 'Checkout';

    button.dataset.holsGiftLoading = 'true';
    button.dataset.holsGiftOriginalText = originalText;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Preparing Checkout...';

    try {
      let cart = await getCart();

      if (!hasPaidProducts(cart)) {
        button.dataset.holsGiftLoading = 'false';
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
        return false;
      }

      cart = await removeAllSamples(cart);

      const sampleId = selectedSampleId() || randomSampleId();

      await addSample(sampleId);

      window.location.href = '/checkout';
    } catch (error) {
      console.error(error);
      button.dataset.holsGiftLoading = 'false';
      button.removeAttribute('aria-busy');
      button.textContent = originalText;
      alert('Checkout is taking too long. Please try again.');
    }

    return false;
  }

  window.holsGiftCheckout = prepareCheckout;
  window.holsGiftSelectSample = selectSample;
  window.holsGiftSyncCartState = syncSamples;

  document.addEventListener(
    'change',
    (event) => {
      if (event.target.matches('#holsGiftFirst')) {
        selectSample(event.target.value);
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('#checkout, .cart__checkout-button, a[href="/checkout"], button[name="checkout"]');

      if (!button || button.closest('.additional-checkout-buttons')) return;

      prepareCheckout(event, button);
    },
    true
  );

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === 'holsGiftSamples') return;

    const cart = event.detail?.resource;

    if (cart?.items) {
      fixDrawerEmptyState(cart);
    }

    repairDrawerStateSoon();
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('#checkout, .cart__checkout-button').forEach((button) => {
      button.dataset.holsGiftLoading = 'false';
      button.removeAttribute('aria-busy');

      if (button.dataset.holsGiftOriginalText) {
        button.textContent = button.dataset.holsGiftOriginalText;
      }
    });

    repairDrawerStateSoon();
    syncSamples();
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        repairDrawerStateSoon();
        syncSamples();
      },
      { once: true }
    );
  } else {
    repairDrawerStateSoon();
    syncSamples();
  }
})();